import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Mail } from 'lucide-react';
import { useFirebaseEmail } from '@/hooks/useFirebaseEmail';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'react-hot-toast';

const EmailModal = ({ isOpen, onClose, recipientEmail = '', recipientUid = null }) => {
  const { currentUser } = useAuth();
  const { sendEmailByAdmin, loading, error } = useFirebaseEmail();
  
  const [to, setTo] = useState(recipientEmail);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTo(recipientEmail);
      setSubject('');
      setEmailBody('');
    }
  }, [isOpen, recipientEmail]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleSendEmail = async () => {
    if (!to.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }
    
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    if (!emailBody.trim()) {
      toast.error('Please enter email content');
      return;
    }

    if (!currentUser?.uid) {
      toast.error('You must be logged in to send emails');
      return;
    }

    try {
      // Use sendEmailByAdmin which expects an array of UIDs
      if (recipientUid) {
        // Convert plain text to HTML format for proper email rendering
        const htmlContent = {
          title: subject,
          text: emailBody.replace(/\n/g, '<br>') // Convert line breaks to HTML
        };
        
        await sendEmailByAdmin([recipientUid], subject, emailBody, htmlContent);
        toast.success('Email sent successfully!');
      } else {
        // For now, we'll show an error if no UID is provided
        // You may want to implement a different email sending function for arbitrary emails
        toast.error('Recipient UID is required for sending emails');
        return;
      }
      
      onClose();
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error(`Failed to send email: ${err.message}`);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 overflow-hidden touch-none pointer-events-auto">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-3xl overflow-hidden overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6 text-blue-500" />
            <span className="font-bold text-xl text-gray-900">Send Email</span>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 bg-gray-50">
          {/* To Field */}
          <div className="mb-4">
            <label className="block mb-2 font-medium text-gray-700 text-base">
              To:
            </label>
            <input
              type="email"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter recipient email address"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Subject Field */}
          <div className="mb-4">
            <label className="block mb-2 font-medium text-gray-700 text-base">
              Subject:
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={loading}
              maxLength={200}
            />
          </div>

          {/* Email Body */}
          <div className="mb-4">
            <label className="block mb-2 font-medium text-gray-700 text-base">
              Email Body:
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              placeholder="Write your email content here..."
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              disabled={loading}
              rows={8}
              maxLength={5000}
            />
          </div>

          {error && (
            <div className="text-red-500 mb-3 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            className="px-4 py-2 rounded-lg text-base text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white text-base font-semibold hover:bg-blue-600 transition-colors disabled:opacity-60"
            onClick={handleSendEmail}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmailModal; 